import React from 'react';
import Container from 'components/Container';
import { H1 } from 'components/Typography';
import Status from 'components/Status';
import {
  Background,
  UpdatedOn,
  Section,
  Heading,
  Paragraph,
  Box,
  BoxColumn,
  List,
  Email,
  SubHeader
} from './styled';

const PrivacyPolicy = () => {
  return (
    <Container bg="white" variant="dark">
      <Background>
        <H1>SafetyWing Customer Privacy Policy</H1>
        <UpdatedOn>Last updated on September 2025</UpdatedOn>

        <Section>
          <Heading>1. Introduction</Heading>
          <Paragraph>
            At SafetyWing, we take the safe and confidential handling of your Personal
            Data seriously, and we want you to understand how we are doing this and make
            you fully aware of your rights.
          </Paragraph>
          <Paragraph>
            We have drafted our Privacy Policy to be simple, clear, and concise, so that
            we accurately describe how we are collecting and using the Personal Data we
            collect.
          </Paragraph>
          <Paragraph>
            Building a global social safety net is a lot of work and requires us to
            collect some Personal Data from our customers. Through our Data Privacy
            controls, we take good care of all the Personal Data we collect.
          </Paragraph>
          <Paragraph>
            The overall approach we follow at SafetyWing is that:
            <ul>
              <li>
                We only process Personal Data when there is a valid reason (“lawfulness”).
              </li>
              <li>
                We are always transparent about our intentions with Personal Data
                ("transparency").
              </li>
              <li>
                We only collect what we need and as much as we need, not a letter more
                (“purpose limitation” and “data minimisation”).
              </li>
              <li>
                We only keep data as long as we need it, then it is deleted (“storage
                limitation”).
              </li>
              <li>
                We keep all Personal Data secure and do not sell your data to anyone.
              </li>
              <li>We make every effort to respect your data rights.</li>
            </ul>
            While we have put care and attention into making this policy as clear as
            possible, it's ok to have questions and we are here to help. Feel free to
            reach out to our customer support team or Data Protection Officer (
            <Email href="mailto:dpo@safetywing.com">dpo@safetywing.com</Email>) and they
            will be more than happy to help.
          </Paragraph>
        </Section>

        <Section>
          <Heading>2. Why do we need your data anyway? (Purpose of processing)</Heading>
          <Paragraph>That's a good question. Well, for a few reasons actually.</Paragraph>
          <Paragraph>
            We want to provide you with the best service possible, answer all your
            questions in a specific and useful way, make sure that our products and
            services are suitable for you, and ultimately to enter into a contract with
            you to provide that service. To do all this, we need your Personal Data.
          </Paragraph>
        </Section>

        <Section>
          <Heading>3. TL;DR (The short version)</Heading>
          <Paragraph>
            Here's a brief, plain-language summary of what you want to know:
            <ul>
              <li>
                We only collect the Personal Data that we need to provide you with
                products and services.
              </li>
              <li>
                In most cases, we process your data based on a valid reason ("lawful
                basis"), contractual or legal obligations for which we do not need your
                consent.
              </li>
              <li>
                Personal Data is collected either directly from customers or from related
                third parties.
              </li>
              <li>
                Sensitive data (special categories of Personal Data) might be collected in
                specific situations, and will be handled with extra care.
              </li>
              <li>
                We usually keep the Personal Data of customers for 7 years following the
                expiration of their policies.
              </li>
              <li>
                There are international transfers of Personal Data, as you would expect
                from a global company like SafetyWing.
              </li>
              <li>You have rights!</li>
            </ul>
          </Paragraph>
        </Section>

        <Section>
          <Heading>4. A few definitions to make the reading easier</Heading>
          <Paragraph>
            <ul>
              <li>
                <b>Personal Data:</b> Any information that can identify a natural person
                (i.e. human)
              </li>
              <li>
                <b>(Personal Data) Processing:</b> Collection, recording, organization,
                structuring, storage, adaptation or alteration, retrieval, consultation,
                use, disclosure by transmission, dissemination or otherwise making
                available, alignment or combination, restriction, erasure or destruction
                of Personal Data.
              </li>
              <li>
                <b>Data Controller:</b> An organization that decides how the Personal Data
                it collects and holds is used and dictates how other companies or
                individuals ("data processors") should process Personal Data.
              </li>
              <li>
                <b>Data Processor:</b> A person (natural or legal) that processes Personal
                Data of which it has no ownership according to the instructions provided
                by the Data Controller.
              </li>
              <li>
                <b>Special Categories of Personal Data:</b> Personal data revealing
                sensitive information including but not limited to genetic data, biometric
                data, data concerning health or data concerning a natural person's sex
                life or sexual orientation.
              </li>
              <li>
                <b>Lawful basis:</b> A valid, legitimate reason to process Personal Data.
              </li>
            </ul>
          </Paragraph>
        </Section>

        <Section>
          <Heading>5. Let's start with the what, why, and where</Heading>
          <Paragraph>
            <b>What</b> data do we collect, <b>why</b> do we collect it, and <b>where</b>{' '}
            do we get it from?
          </Paragraph>
          <Paragraph>
            We want to make sure that we know our customers well so that we can help you
            get the best experience with SafetyWing. To do this, we need to collect a few
            details about you, as presented below.
          </Paragraph>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <List>
                Identification details:
                <li>Full name</li>
                <li>Gender</li>
                <li>Address</li>
                <li>Date of birth</li>
                <li>Home country, citizenship, nationality</li>
                <li>Occupation</li>
                <li>
                  National identifier numbers (including passport number and/or driver's
                  license number)
                </li>
                <li>Email address and phone number</li>
              </List>
            </BoxColumn>
            <BoxColumn>
              <b>Why do we need it</b>
              <List>
                We <b>only</b> use this to identify you and:
                <li>To open and manage your accounts and relationship with us</li>
                <li>To verify your age</li>
                <li>To enhance your experience and our products and services</li>
                <li>
                  To share relevant marketing about our products and services, news and
                  updates, and upcoming events (if you consent to it)
                </li>
                <li>To meet our legal and regulatory obligations</li>
                <li>To enable you to register for an online account</li>
                <li>To contact you about your account(s)</li>
                <li>
                  To determine your eligibility for some of our products and services
                </li>
              </List>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <List>
                Family details:
                <li>Marital status</li>
                <li>Next of kin</li>
                <li>Dependents</li>
              </List>
            </BoxColumn>
            <BoxColumn>
              <b>Why do we need it</b>
              <Paragraph>
                To understand your circumstances and needs and assess the suitability of
                products and services you apply for with us or one of our partners.
              </Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <Paragraph>Data on how you interact with us</Paragraph>
            </BoxColumn>
            <BoxColumn>
              <b>Why do we need it</b>
              <Paragraph>
                To give you the best service and prevent fraud. This can include whether
                you have carried out a transaction over the phone or over the internet.
              </Paragraph>
            </BoxColumn>
          </Box>

          <Paragraph>
            <b>Where do we get it from</b>
          </Paragraph>
          <Paragraph>
            We get this data either directly from you or through third parties in cases
            where someone else adds you as a dependent or member of a policy that you do
            not own.
          </Paragraph>

          <Paragraph>
            More precisely, we will obtain data directly from you via:
          </Paragraph>

          <List>
            <li>
              <b>Registration & Purchase</b>
              <List>
                <li>
                  When you sign up for the first time and make your first purchase is when
                  we initially collect most of your Personal Data.
                </li>
                <li>
                  When you update your Personal Data online or you request that we make
                  changes. We process your Personal Data by updating it to be accurate.
                </li>
              </List>
            </li>
            <li>
              <b>Phone communications</b>
              <List>
                <li>
                  When you speak to us on the phone, we may record the call for training
                  and quality purposes.
                </li>
              </List>
            </li>
            <li>
              <b>Website and application use</b>
              <List>
                <li>
                  When using our websites, and any digital or mobile app we may offer now
                  or in the future.
                </li>
              </List>
            </li>
            <li>
              <b>Written communication</b>
              <List>
                <li>
                  When you reach out to us through our customer support chat services, the
                  conversation is stored in our records. When you send us emails, we store
                  those in our records as well.
                </li>
                <li>
                  When we use information that you've made public, such as social media
                  content or when you interact with our social media profiles.
                </li>
              </List>
            </li>
            <li>
              <b>Data collected from third parties</b>
              There are also times when personal data will come from a third party. For
              example:
              <List>
                <li>
                  If you are signing up as a joint account with someone else, and you are
                  not the main policyholder, we will collect your Personal Data when the
                  policyholder signs you up.
                </li>
                <li>
                  When a nominated representative signs you up. Since the signup will be
                  about you, we will collect your Personal Data.
                </li>
              </List>
            </li>
          </List>
        </Section>

        <Section>
          <Heading>
            6. You may be wondering on what grounds we are collecting Personal Data
            (“Lawfulness of processing”)
          </Heading>
          <Paragraph>
            We collect your data for a few reasons, and most of them do not require us to
            get consent from you. This means that a) we do not need to bother you with
            giving us consent and b) we really need this data to provide our Products and
            Services.
          </Paragraph>
          <Paragraph>
            According to data privacy frameworks, Personal Data can only be collected
            where there are valid reasons. In the absence of any other legal reason
            (lawful basis), we need to obtain your consent to process your data.
          </Paragraph>
          <Paragraph>
            These are the grounds on which we are collecting data from you ("lawful
            bases"):
          </Paragraph>
          <Box>
            <BoxColumn>
              <b>Lawful basis</b>
              <Paragraph>Consent</Paragraph>
              <Status variant="yellow" text="Less frequently used" />
            </BoxColumn>
            <BoxColumn>
              <b>Description</b>
              <Paragraph>
                This is when we ask you to confirm whether you are ok for us to process
                your Personal Data for a specific purpose.
              </Paragraph>
              <Paragraph>
                For example, when you sign up with SafetyWing, we ask whether you consent
                to receiving info about our new product releases, fun campaigns, and other
                news. We will only contact you about this kind of information if you check
                that box.
              </Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Lawful basis</b>
              <Paragraph>Explicit Consent</Paragraph>
              <Status variant="yellow" text="Less frequently used" />
            </BoxColumn>
            <BoxColumn>
              <b>Description</b>
              <Paragraph>
                This is when we process any of your sensitive information (special
                categories of Personal Data), where we explicitly obtain your consent for
                the processing.
              </Paragraph>
              <Paragraph>
                For example, when you are submitting a claim, we ask whether you consent
                to us processing your claim-related information to assess the claim.
              </Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Lawful basis</b>
              <Paragraph>Contract</Paragraph>
              <Status variant="purple" text="More frequently used" />
            </BoxColumn>
            <BoxColumn>
              <b>Description</b>
              <Paragraph>
                This is when we need to process your Personal Data so that we can execute
                our contract with you to provide you with the service. Unless we process
                the data, a contract cannot exist.
              </Paragraph>
              <Paragraph>
                For example, when you purchase either Nomad Insurance or Remote Health
                products, we need to take your name, age, and other demographic
                information to be able to provide you with the products themselves.
              </Paragraph>
              <Paragraph>In this circumstance, your consent is not required.</Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Lawful basis</b>
              <Paragraph>Legal Obligation</Paragraph>
              <Status variant="purple" text="More frequently used" />
            </BoxColumn>
            <BoxColumn>
              <b>Description</b>
              <Paragraph>
                This is when we need to process your Personal Data in order to comply with
                the law.
              </Paragraph>
              <Paragraph>
                This may come up when we have a reporting obligation to a regulatory
                authority.
              </Paragraph>
              <Paragraph>Again, in this circumstance, no consent is required.</Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Lawful basis</b>
              <Paragraph>Legitimate Interest</Paragraph>
              <Status variant="purple" text="More frequently used" />
            </BoxColumn>
            <BoxColumn>
              <b>Description</b>
              <Paragraph>
                This is when processing is necessary for our legitimate interests or the
                legitimate interests of a third party, i.e., valid reasons.
              </Paragraph>
              <Paragraph>
                For example, we might need to process your Personal Data for fraud
                prevention purposes.
              </Paragraph>
              <Paragraph>
                The legitimate interests on which we will process your personal data are:
                <ul>
                  <li>Fraud prevention</li>
                  <li>Sanctions screening</li>
                </ul>
              </Paragraph>
              <Paragraph>No consent is required from you here either.</Paragraph>
            </BoxColumn>
          </Box>
        </Section>

        <Section>
          <Heading>
            7. Does SafetyWing need sensitive Personal Data? ("Processing of special
            categories of Personal Data")
          </Heading>

          <Paragraph>
            At SafetyWing, sensitive data usually means health data such as medical
            history, claims information, vaccination history, and any other information
            relevant to your coverage, policy, or claim. If we need to collect or process
            any sensitive data, you can rest assured that we will let you know about this
            in advance. Not only will you know, but you will also be asked whether you
            approve of SafetyWing processing any Personal Data ("explicit consent").
          </Paragraph>
          <Paragraph>
            In the event that you submit information to us that involves any sensitive
            data, thereby granting us access to that sensitive data, we will ask for your
            consent to process the data, and will also handle it with additional security,
            as this is what the laws require us to do.
          </Paragraph>
          <Paragraph>
            We understand and appreciate that providing sensitive data is sometimes
            difficult. So please know that at SafetyWing, access to sensitive data is
            limited to a need-to-know basis.
          </Paragraph>
        </Section>

        <Section>
          <Heading>8. What about Personal Data regarding children?</Heading>
          <Paragraph>
            Although we don’t offer products directly to children, we may happen to become
            recipients of such data when you inquire about a family policy and you wish
            for your children to be covered. When we do collect this information, we will
            only ask for the minimal amount of data to enable an accurate quotation for
            coverage. Any data, like all the information we store, will be protected and
            kept secure, and the only people able to access it will be those who strictly
            need to in order to help with your request.
          </Paragraph>
        </Section>

        <Section>
          <Heading>9. What's SafetyWing's role when processing my personal data?</Heading>
          <Paragraph>
            SafetyWing will always be the <b>Data Controller</b> for any personal data
            collected from customers. This means that we will be responsible for how your
            data is processed, including cases where we need to transfer your data to
            third parties.
          </Paragraph>
          <Paragraph>
            In other words, even when your data is transferred to third parties, we are
            still responsible for it.
          </Paragraph>
        </Section>

        <Section>
          <Heading>
            10. Why does SafetyWing give my Personal Data to third parties?! (“transfers
            to third parties & international transfers”)
          </Heading>
          <Paragraph>
            As the saying goes, it takes a village, and this is very true in building a
            global social safety net. In order to provide our Products and Services, we
            sometimes need to share your data with third parties, and this sometimes
            involves international transfers of data. Let's address two important
            questions:
          </Paragraph>
          <List>
            <li>Where does your personal data travel to?</li>
            <li>How do we protect your data through this process?</li>
          </List>
          <SubHeader>10.1. Let's start with where.</SubHeader>
          <Paragraph>
            Personal data is shared with the companies we use to provide, promote and
            protect our products, and our (re)insurance partners who, in some cases, are
            the ones that enable us to provide innovative new products.
          </Paragraph>
          <Paragraph>
            In both cases, Personal Data might be shared internationally, as our vendors
            might have data hosted in different locations.
          </Paragraph>
          <List>
            Some examples of where we share your data to:
            <li>Our insurance partners</li>
            <li>
              Providers we are using to perform compliance, security and identity checks
              according to what insurance regulations require us to do
            </li>
            <li>
              Cloud service providers we use to securely store all data, including
              Personal Data
            </li>
            <li>
              Communication service providers to be able to provide you with real time and
              immediate communication with our customer care team
            </li>
            <li>Payment service providers</li>
            <li>Global assistance providers companies</li>
            <li>
              Government agencies, in certain cases where it is necessary to provide you
              with the service or product you have requested.
            </li>
          </List>

          <SubHeader>
            10.2. How do you protect my data when transferred internationally?
          </SubHeader>
          <List>
            <li>
              First and most importantly, by ensuring that we only work with vendors and
              partners that treat data securely.
            </li>
            <li>
              We review our vendors and partners each year to ensure they maintain strong
              security standards.
            </li>
            <li>
              We only transfer Personal Data to countries that we have assessed and
              concluded that they have sufficient data protection frameworks.
            </li>
            <li>
              We put the necessary standard contractual clauses (as per Data Privacy
              regulations) in our contracts with third parties to ensure that they will do
              what the data protection law requires to provide us with adequate data
              privacy and security levels.
            </li>
          </List>
        </Section>

        <Section>
          <Heading>
            11. What categories of Personal Data do we collect from third parties?
          </Heading>

          <Paragraph>
            <b>Identification and Sanctions Screening:</b>
          </Paragraph>
          <List>
            <li>
              We process data from public records, government databases, sanctions lists,
              and regulatory bodies to verify that individuals or entities aren't under
              economic or trade sanctions. We do this to ensure compliance, prevent
              illicit activities, and promote ethical business practices.
            </li>

            <li>
              The most usual categories of Personal Data we might come across when
              performing these activities are First Name, Last Name, and Date of Birth, as
              well as alternate names/aliases where these are applicable.
            </li>
          </List>
          <Paragraph>
            <b>Medical and Health Information</b>
          </Paragraph>
          <List>
            <li>
              Medical records, history, or details related to health conditions and
              treatments obtained from healthcare providers or medical examiners. This can
              be obtained only after explicit consent, which you are requested to provide
              when submitting a claim.
            </li>
          </List>

          <Paragraph>
            <b>Employment and Professional Information</b>
          </Paragraph>
          <List>
            <li>
              Job status, title, and employment details collected from employers, required
              for eligibility checks.
            </li>
          </List>
        </Section>

        <Section>
          <Heading>12. How does SafetyWing make sure my Personal Data is secure?</Heading>
          <List>
            We treat your data like ours. And for this reason, we take the security of
            Personal Data seriously and have the necessary measures in place:
            <li>
              We keep all of our data, including your Personal Data, collected on secure
              cloud locations (Google Cloud Platform).
            </li>
            <li>
              We ensure that only a limited number of people have access to your Personal
              Data, on a need-to-know basis.
            </li>
            <li>
              We review our data privacy and security infrastructure each year, using
              independent audits to ensure that we are keeping up with global regulatory
              requirements and best practices.
            </li>
            <li>
              We have procedures in place in case of a data breach, which include
              notifying the affected users where relevant.
            </li>
          </List>
          <SubHeader>12.1. Where does SafetyWing store my data?</SubHeader>
          <Paragraph>
            SafetyWing stores your data, using reliable cloud services providers, on
            servers located in the European Union.
          </Paragraph>
        </Section>

        <Section>
          <Heading>
            13. Do I have any rights on the processing of my data ("Data Subject's
            rights")?
          </Heading>
          <Paragraph>Of course you do, and we explain everything below.</Paragraph>
          <Paragraph>
            You can exercise your rights by sending an email to{' '}
            <Email href="mailto:dpo@safetywing.com">dpo@safetywing.com</Email> or reaching
            out to our Customer Care team through our website.
          </Paragraph>
          <Paragraph>
            We always try to address your request <b>within 1 month of receiving them</b>,
            and in the event we will need more time, we will let you know.
          </Paragraph>
          <SubHeader>
            13.1. First of all, you have the right to know what personal data we are
            holding related to you (“Right of access”).
          </SubHeader>
          <Paragraph>
            When you submit a Subject Access Request, we will tell you what personal data
            we have about you, whether these are transferred to third parties, the
            duration we intend to keep the data in our records, whether we use your data
            to perform automated profiling, and other details explicitly related to you.
          </Paragraph>
          <Paragraph>
            Just keep in mind that we <b>cannot</b> execute a request that does not have
            reasonable grounds, or one that is regarding a person other than you.
          </Paragraph>
          <SubHeader>
            13.2 You can request that we update your personal data if it is not accurate
            (“Right of rectification”).
          </SubHeader>
          <Paragraph>
            Do you believe that personal data we have related to you is not correct? Just
            let us know and we will explain the process to update everything to be
            correct.
          </Paragraph>
          <SubHeader>
            13.3. You can ask for your personal data to be deleted (“Right of erasure”).
          </SubHeader>
          <Paragraph>
            You can request that we delete some or all of your data, and we will gladly
            comply, to the best of our abilities.
          </Paragraph>
          <Paragraph>
            While you have the right to request for your data to be deleted, we also have
            a legal obligation to keep the data for a certain period of time (“retention
            schedule”). When we receive such a request from you, we will let you know what
            data we can delete now, and what data we will need to wait to delete.
          </Paragraph>
          <SubHeader>
            13.4. You can request that we restrict the processing of your personal data
            (“Right to restriction of processing”).
          </SubHeader>
          <Paragraph>
            If you would like us to stop processing your personal data any further
            (excluding the storing of your data, as this is still required), you can
            request this. We will review your request and if our legal obligations allow
            us to execute your request, we will happily do so.
          </Paragraph>
          <SubHeader>
            13.5. You can ask that we send your personal data to another third party
            (“Right to data portability”).
          </SubHeader>
          <Paragraph>
            According to this right, you can ask that we send any of your personal data we
            keep in a structured, commonly used and machine-readable format to another
            Data Controller.
          </Paragraph>
          <SubHeader>
            13.6. You have the right to request that we do not make any automated
            decisions based on your personal data (“Right to object to automated
            individual decision-making”).
          </SubHeader>
          <Paragraph>
            In cases where your Personal Data is used to make automated decisions, you
            have the right to object to it and the process will stop, unless there are
            other pressing reasons, which will be communicated to you.
          </Paragraph>
          <SubHeader>
            13.7. You have the right to withdraw your consent ("Conditions for consent")
          </SubHeader>
          <Paragraph>
            You have the right to withdraw your consent at any time. This won't affect the
            lawfulness of processing done before you withdrew your consent.
          </Paragraph>
          <Paragraph>
            As mentioned above, you can exercise your rights or ask for more information
            about them by sending an email to{' '}
            <Email href="mailto:dpo@safetywing.com">dpo@safetywing.com</Email> or
            contacting us through our 24/7 customer care chat on our website.
          </Paragraph>
          <Paragraph>
            Please keep in mind that exercising your right to withdraw consent will only
            affect the processing of Personal Data for which the lawful basis has been the
            consent. Processing of Personal Data performed using other lawful bases is not
            affected by the withdrawal of consent.
          </Paragraph>
        </Section>

        <Section>
          <Heading>14. Compliance with data protection laws</Heading>
          <Paragraph>
            Our privacy policy primarily complies with the General Data Protection
            Regulation (GDPR) as the guiding framework for our data protection practices.
            We strive to align with the principles and requirements set out by the GDPR to
            ensure your data is handled securely, transparently, and in line with your
            rights, while we also comply with other global data protection frameworks.
          </Paragraph>
          <Paragraph>
            If you have any questions about how we comply with data protection laws or
            would like further information on your rights under GDPR, feel free to contact
            us.
          </Paragraph>
        </Section>

        <Section>
          <Heading>15. Right to lodge a complaint</Heading>
          <Paragraph>
            If you believe your data privacy rights have been violated, you have the right
            to lodge a complaint.
          </Paragraph>
          <Paragraph>
            If you're not happy with anything related to the Privacy Policy, we are here
            to help. Here's what you can do depending on what the issue is:
          </Paragraph>
          <List>
            <li>
              You can reach out to us and express your concerns or complaints by sending
              an email to{' '}
              <Email href="mailto:feedback@safetywing.com">feedback@safetywing.com</Email>
            </li>
            <li>
              You can contact our Data Protection Officer by sending an email to{' '}
              <Email href="mailto:dpo@safetywing.com">dpo@safetywing.com</Email>
            </li>
          </List>
          <Paragraph>
            Alternatively, you can file a complaint with a supervisory authority by
            sending an email to{' '}
            <Email href="mailto:commissioner@dataprotection.gov.cy">
              commissioner@dataprotection.gov.cy
            </Email>
            .
          </Paragraph>

          <SubHeader>15.1 SafetyWing's EU representative</SubHeader>
          <Paragraph>
            SafetyWing (Managing General Agent) Ltd (Reg. No. TC.050364, Turks and Caicos
            Islands) has appointed an EU Representative established in Cyprus, in
            accordance with art. 27 of the General Data Protection Regulation (EU)
            2016/679 (GDPR). If you are located in the European Union, you may contact our
            EU Representative with any questions about the processing of your Personal
            Data by SafetyWing (Managing General Agent) Ltd including requests to exercise
            your rights under GDPR, using the following contact details:
          </Paragraph>
          <Paragraph>
            <b>EU Representative's Contact Details:</b>
            <br />
            RF PRIVACY MINDERS LTD
            <br />
            33 Konstantinou Paleologou, The Square, 2nd Floor,
            <br />
            CY-6036, Larnaca, Cyprus
            <br />
            E-mail:{' '}
            <Email href="mailto:safetywing.eu@privacyminders.com">
              safetywing.eu@privacyminders.com
            </Email>
            <br />
            Tel.: +357 24 323333
          </Paragraph>
        </Section>

        <Section>
          <Heading>16. How long do you keep my data for ("Retention schedule")?</Heading>
          <Paragraph>Only for as long as we need to, and not a day more.</Paragraph>
          <List>
            We keep data as long as laws and regulations require us to. Some of the
            factors we take into account to decide on how long to hold your data
            (“retention period”) are:
            <li>
              Customer expectations, the nature of your relationship with us, your
              membership status and the types of accounts, products and services you have
              with us.
            </li>
            <li>
              The maximum or minimum retention periods identified by legal or regulatory
              guidance.
            </li>
            <li>Our contractual rights and obligations.</li>
            <li>
              Forensic requirements, for example, the need to access data no longer
              actively used in order to manage or respond to a complaint or dispute.
            </li>
            <li>
              The risks involved in retention, deletion or removal, and the cost of
              maintaining, storing, archiving, and retrieving data.
            </li>
            <Paragraph>
              You can see below a more detailed presentation of how long we keep different
              types of data:
            </Paragraph>
          </List>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <List>
                <li>Name</li>
                <li>Title (e.g. Ms, Mr)</li>
                <li>Sex</li>
                <li>Address</li>
                <li>Date of birth</li>
                <li>Nationality</li>
                <li>Occupation</li>
                <li>
                  National identifier numbers, passport numbers, driver's license number
                </li>
                <li>Email address and phone number</li>
                <li>Family details, marital status, next of kin, dependents</li>
              </List>
            </BoxColumn>
            <BoxColumn>
              <b>How long we keep it for</b>
              <Paragraph>
                5-7 years after the end of our business relationship, depending on the
                applicable retention period of the dataset itself.
              </Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <Paragraph>Call recordings</Paragraph>
            </BoxColumn>
            <BoxColumn>
              <b>How long we keep it for</b>
              <Paragraph>3 years after the end of our business relationship.</Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <List>
                <li>Geo-location data</li>
                <li>How you interact with us</li>
              </List>
            </BoxColumn>
            <BoxColumn>
              <b>How long we keep it for</b>
              <Paragraph>1 year after the end of our business relationship.</Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <Paragraph>Direct marketing preferences</Paragraph>
            </BoxColumn>
            <BoxColumn>
              <b>How long we keep it for</b>
              <Paragraph>
                Until you tell us you no longer wish to receive marketing.
              </Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <Paragraph>Enquiries only</Paragraph>
            </BoxColumn>
            <BoxColumn>
              <b>How long we keep it for</b>
              <Paragraph>
                Personal Data of people who expressed enquiries without making any
                purchases is usually kept for 3 months.
              </Paragraph>
            </BoxColumn>
          </Box>
        </Section>

        <Section>
          <Heading>
            17. What happens after the Retention schedule? ("Disposal of Personal Data")
          </Heading>
          <Paragraph>
            As we pointed out earlier, we are very careful about how we deal with your
            Personal Data, including how long we will keep it for. So when we no longer
            need to keep your Personal Data, we will politely and gently show them the
            door as they will no longer be welcome at SafetyWing's servers.
          </Paragraph>
          <Paragraph>
            To be more precise, once the retention period is up, we will make sure that:
          </Paragraph>
          <List>
            <li>
              All Personal Data associated with your name is deleted or anonymised from
              our servers. When we say anonymise, we mean that there will be no way for
              anyone, not even Superman, to know who the data used to belong to. The
              anonymisation will be completely irreversible, so that we are in line with
              what the Data Privacy frameworks dictate.
            </li>
            <li>
              Your Personal Data will be deleted from third-party vendors we are using,
              excluding any information that they are legally required to retain, so that
              they won't get into trouble.
            </li>
            <li>
              You will not hear from us, unless you explicitly tell us otherwise (which we
              hope you will!)
            </li>
            <li>
              We will reach out to you and let you know that we will be deleting your
              information shortly, so that you can either accept deletion or advise
              otherwise.
            </li>
          </List>
          <Paragraph>
            This might be the end of our friendship, but we will always hope that you will
            come back and visit every now and then!
          </Paragraph>
        </Section>

        <Section>
          <Heading>18. Can we sign a Data Processing Agreement with SafetyWing?</Heading>
          <Paragraph>
            Most certainly, just reach out to our Customer Care team, or send an email to{' '}
            <Email href="mailto:dpo@safetywing.com">dpo@safetywing.com</Email> and we will
            take care of the rest.
          </Paragraph>
        </Section>

        <Section>
          <Heading>19. Phew, that's it!</Heading>
          <Paragraph>
            So, that was our Privacy Policy. Thank you for reading through all of it!
          </Paragraph>
          <Paragraph>
            We really hope that we have answered any questions you might have had, and
            have given you enough information to understand both what we do with your
            Personal Data as well as how we do it.
          </Paragraph>
          <Paragraph>
            If you still have questions or concerns, please reach out to our Data
            Protection Officer by email at{' '}
            <Email href="mailto:dpo@safetywing.com">dpo@safetywing.com</Email> or contact
            us via our 24/7 live chat.
          </Paragraph>
        </Section>
      </Background>
    </Container>
  );
};

export default PrivacyPolicy;
